import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  KeyboardContainer,
  KeyboardRow,
  KeyboardKey,
  KeyboardBackspace,
  KeyboardClear,
  KeyboardOverlay,
  KeyboardCloseHeader,
  KeyboardCapsLock,
  KeyboardShift,
  KeyboardSpecial,
  KeyboardSpace,
  KeyboardEnter,
} from "./styles";

const KEYBOARD_LAYOUT = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
  ["Z", "X", "C", "V", "B", "N", "M"],
];

const SPECIAL_CHARS_LAYOUT = [
  ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"],
  ["-", "_", "=", "+", "[", "]", "{", "}", "\\", "|"],
  [";", ":", "'", '"', ",", ".", "<", ">", "/", "?"],
  ["~", "`"],
];

const NUMBER_ROW_SPECIAL = ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"];

const VirtualKeyboard = ({
  isVisible,
  onKeyPress,
  onBackspace,
  onClear,
  onClose,
  onEnter,
  value = "",
}) => {
  const intl = useIntl();
  const [isCapsLock, setIsCapsLock] = useState(false);
  const [isShift, setIsShift] = useState(false);
  const [showSpecialChars, setShowSpecialChars] = useState(false);

  const getCurrentLayout = useCallback(() => {
    if (showSpecialChars) {
      return SPECIAL_CHARS_LAYOUT;
    }
    return KEYBOARD_LAYOUT;
  }, [showSpecialChars]);

  const getKeyValue = useCallback(
    (key, rowIndex) => {
      if (showSpecialChars) {
        return key;
      }

      // Para a primeira linha (números), mostra caracteres especiais se shift estiver ativo
      if (rowIndex === 0 && isShift && !isCapsLock) {
        const index = KEYBOARD_LAYOUT[0].indexOf(key);
        if (index !== -1) {
          return NUMBER_ROW_SPECIAL[index];
        }
      }

      // Para letras, aplica maiúsculo/minúsculo
      if (typeof key === "string" && /[A-Z]/.test(key)) {
        if (isCapsLock || isShift) {
          return key.toUpperCase();
        } else {
          return key.toLowerCase();
        }
      }

      return key;
    },
    [showSpecialChars, isShift, isCapsLock]
  );

  const handleKeyClick = useCallback(
    (key) => {
      const keyValue = getKeyValue(
        key,
        getCurrentLayout().findIndex((row) => row.includes(key))
      );
      onKeyPress(keyValue);

      // Se shift estiver ativo (não caps lock), desativa após pressionar uma tecla
      if (isShift && !isCapsLock) {
        setIsShift(false);
      }
    },
    [onKeyPress, getKeyValue, getCurrentLayout, isShift, isCapsLock]
  );

  const handleCapsLockToggle = useCallback(() => {
    setIsCapsLock(!isCapsLock);
    setIsShift(false); // Desativa shift ao ativar caps lock
  }, [isCapsLock]);

  const handleShiftToggle = useCallback(() => {
    setIsShift(!isShift);
    setIsCapsLock(false); // Desativa caps lock ao ativar shift
  }, [isShift]);

  const handleSpecialCharsToggle = useCallback(() => {
    setShowSpecialChars(!showSpecialChars);
  }, [showSpecialChars]);

  const handleBackspaceClick = useCallback(() => {
    onBackspace();
  }, [onBackspace]);

  const handleClearClick = useCallback(() => {
    onClear();
  }, [onClear]);

  const handleCloseClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleEnterClick = useCallback(() => {
    if (onEnter) {
      onEnter();
    }
    onClose();
  }, [onEnter, onClose]);

  // Evita que cliques no teclado virtual fechem o teclado
  const handleKeyboardClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <KeyboardOverlay isVisible={isVisible}>
      <KeyboardContainer onClick={handleKeyboardClick}>
        <KeyboardCloseHeader onClick={handleCloseClick}>×</KeyboardCloseHeader>

        {getCurrentLayout().map((row, rowIndex) => (
          <KeyboardRow key={rowIndex}>
            {/* Adiciona Caps Lock antes da segunda linha (A, S, D...) */}
            {rowIndex === 2 && (
              <KeyboardCapsLock
                onClick={handleCapsLockToggle}
                isActive={isCapsLock}
                type="button"
                title={intl.formatMessage({ id: "virtual_keyboard.caps_lock" })}
              >
                {intl.formatMessage({ id: "virtual_keyboard.caps" })}
              </KeyboardCapsLock>
            )}

            {/* Adiciona Shift antes da última linha de letras (Z, X, C...) */}
            {rowIndex === 3 && !showSpecialChars && (
              <KeyboardShift
                onClick={handleShiftToggle}
                isActive={isShift}
                type="button"
                title={intl.formatMessage({ id: "virtual_keyboard.shift" })}
              >
                ⇧
              </KeyboardShift>
            )}

            {row.map((key) => {
              const keyValue = getKeyValue(key, rowIndex);

              return (
                <KeyboardKey
                  key={key}
                  onClick={() => handleKeyClick(key)}
                  type="button"
                >
                  {keyValue}
                </KeyboardKey>
              );
            })}
          </KeyboardRow>
        ))}

        <KeyboardRow>
          <KeyboardSpecial
            onClick={handleSpecialCharsToggle}
            isActive={showSpecialChars}
            type="button"
          >
            {showSpecialChars ? "ABC" : "#+="}
          </KeyboardSpecial>

          {!showSpecialChars && (
            <KeyboardSpace
              onClick={() => handleKeyClick(" ")}
              type="button"
              title={intl.formatMessage({ id: "virtual_keyboard.space" })}
            >
              {intl.formatMessage({ id: "virtual_keyboard.space" })}
            </KeyboardSpace>
          )}

          {showSpecialChars && (
            <KeyboardSpace
              onClick={() => handleKeyClick(" ")}
              type="button"
              title={intl.formatMessage({ id: "virtual_keyboard.space" })}
            >
              {intl.formatMessage({ id: "virtual_keyboard.space" })}
            </KeyboardSpace>
          )}

          <KeyboardBackspace
            onClick={handleBackspaceClick}
            type="button"
            title={intl.formatMessage({ id: "virtual_keyboard.backspace" })}
          >
            ⌫
          </KeyboardBackspace>

          <KeyboardClear onClick={handleClearClick} type="button">
            {intl.formatMessage({ id: "virtual_keyboard.clear" })}
          </KeyboardClear>

          <KeyboardEnter onClick={handleEnterClick} type="button">
            {intl.formatMessage({ id: "virtual_keyboard.enter" })}
          </KeyboardEnter>
        </KeyboardRow>
      </KeyboardContainer>
    </KeyboardOverlay>
  );
};

export default VirtualKeyboard;
